// frontend/packages/business-console/src/auth/msalConfig.js
/*
 * Updated MSAL configuration for the UpRyzze business console.
 *
 * This version dynamically selects the correct authority based on the
 * Azure B2C configuration.  If a B2C sign‑up/sign‑in policy (either
 * legacy or customer) is defined, the application runs in B2C mode and
 * the policy‑specific authority is used without pinning the metadata.
 * Otherwise the configuration falls back to CIAM (Entra ID) mode and
 * pins the authority metadata to avoid CORS issues.
 */

import { PublicClientApplication, ProtocolMode } from "@azure/msal-browser";
// Import the Azure B2C configuration helper via a relative path.
// Do not rely on @upryzze/shared alias here.
import azureB2C from "../../../shared/config/src/azureB2C";

// Utility functions for trimming values and trailing slashes.
const trim = (v) => String(v || "").trim();
// IMPORTANT: only remove trailing slashes (do NOT touch "https://")
const trimSlash = (s = "") => String(s).replace(/\/+$/, "");

// Determine default API scopes. Always include OIDC scopes.
export const DEFAULT_API_SCOPES = (() => {
  const raw = trim(process.env.REACT_APP_AZURE_B2C_SCOPES);
  if (!raw) return ["openid", "offline_access"];
  const scopes = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const set = new Set([...scopes, "openid", "offline_access"]);
  return Array.from(set);
})();

/**
 * Fetch API scopes from an optional endpoint.  The business console can
 * be configured to retrieve a list of scopes from an API endpoint.  If
 * the endpoint isn't set or fails, this function falls back to
 * DEFAULT_API_SCOPES.
 *
 * The environment variable REACT_APP_AUTH_SCOPES_ENDPOINT overrides the
 * default "/api/auth/scopes".  The endpoint should return either an
 * array of strings or an object with a `scopes` property.
 *
 * @returns {Promise<string[]>} A list of scopes to use when acquiring tokens.
 */
export async function getApiScopes() {
  const endpoint = trim(
    process.env.REACT_APP_AUTH_SCOPES_ENDPOINT || "/api/auth/scopes",
  );
  if (!endpoint) return DEFAULT_API_SCOPES;

  try {
    const resp = await fetch(endpoint, { method: "GET" });
    if (!resp.ok) return DEFAULT_API_SCOPES;
    const data = await resp.json();
    // If the response is an array, assume it's the list of scopes; otherwise
    // look for a `scopes` property on the returned object.
    const scopes = Array.isArray(data) ? data : data?.scopes;
    if (Array.isArray(scopes) && scopes.length) return scopes;
    return DEFAULT_API_SCOPES;
  } catch {
    return DEFAULT_API_SCOPES;
  }
}

let msalInstance = null;

// Environment variables for MSAL configuration.
const clientId = trim(process.env.REACT_APP_MSAL_CLIENT_ID);
const tenantId = trim(process.env.REACT_APP_AZURE_B2C_TENANT_ID);
const baseUrl = trimSlash(
  trim(process.env.REACT_APP_AZURE_B2C_AUTHORITY_BASE_URL),
);

/**
 * Throw an error if a required configuration value is missing.
 * @param {*} value The value to test
 * @param {string} name The environment variable name
 */
function must(value, name) {
  if (!value) {
    const err = new Error(`Missing ${name}`);
    err.code = `MSAL_CONFIG_MISSING_${name}`;
    throw err;
  }
  return value;
}

/**
 * Parse a URL and return its hostname. Returns an empty string for invalid URLs.
 * @param {string} url
 */
function parseHost(url) {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return "";
  }
}

/**
 * Build CIAM authority metadata for tenant‑level endpoints.  When the app
 * operates in CIAM mode, we pin the metadata to avoid cross‑origin
 * discovery calls.  In B2C mode we omit metadata so MSAL can fetch it
 * from the policy‑specific endpoint.
 *
 * @param {string} base The base URL (e.g. https://…ciamlogin.com)
 * @param {string} tid  The tenant ID (GUID)
 */
function buildCiamAuthorityMetadata(base, tid) {
  return JSON.stringify({
    authorization_endpoint: `${base}/${tid}/oauth2/v2.0/authorize`,
    token_endpoint: `${base}/${tid}/oauth2/v2.0/token`,
    end_session_endpoint: `${base}/${tid}/oauth2/v2.0/logout`,
    issuer: `${base}/${tid}/v2.0/`,
    jwks_uri: `${base}/${tid}/discovery/v2.0/keys`,
    response_modes_supported: ["query", "fragment", "form_post"],
    response_types_supported: ["code", "id_token", "code id_token"],
    scopes_supported: ["openid", "profile", "email", "offline_access"],
    subject_types_supported: ["pairwise"],
    token_endpoint_auth_methods_supported: [
      "client_secret_post",
      "private_key_jwt",
      "client_secret_basic",
    ],
  });
}

/**
 * Initialize a PublicClientApplication instance with the correct authority.
 * The instance is cached after the first call.
 *
 * @returns {Promise<PublicClientApplication>} The MSAL instance
 */
export async function initializeMsal() {
  if (msalInstance) return msalInstance;

  // Validate required env vars
  must(clientId, "REACT_APP_MSAL_CLIENT_ID");
  must(tenantId, "REACT_APP_AZURE_B2C_TENANT_ID");
  must(baseUrl, "REACT_APP_AZURE_B2C_AUTHORITY_BASE_URL");

  // Determine redirect URIs, falling back to current origin when in browser context
  const redirectUri =
    trim(process.env.REACT_APP_AZURE_B2C_REDIRECT_URI) ||
    (typeof window !== "undefined"
      ? `${window.location.origin}/auth/callback`
      : "");
  const postLogoutRedirectUri =
    trim(process.env.REACT_APP_AZURE_B2C_POST_LOGOUT_REDIRECT_URI) ||
    (typeof window !== "undefined"
      ? `${window.location.origin}/login`
      : "");

  // Build authority and metadata based on azureB2C configuration
  const { mode: azureMode, defaultAuthority } = azureB2C;
  const knownAuthorities = [];
  let authority;
  let authorityMetadata;

  if (azureMode === "b2c") {
    // B2C mode: use the policy authority and allow MSAL to fetch metadata
    authority = defaultAuthority;
    const host = parseHost(authority);
    if (host) knownAuthorities.push(host);
    authorityMetadata = undefined;
  } else {
    // CIAM mode: use the tenant‑level authority and pin metadata
    authority = `${baseUrl}/${tenantId}`;
    const host = parseHost(baseUrl);
    if (host) knownAuthorities.push(host);
    authorityMetadata = buildCiamAuthorityMetadata(baseUrl, tenantId);
  }

  // Create the PublicClientApplication instance
  const instance = new PublicClientApplication({
    auth: {
      clientId,
      authority,
      knownAuthorities,
      protocolMode: ProtocolMode.OIDC,
      // Only include authorityMetadata when defined (CIAM mode)
      ...(authorityMetadata && { authorityMetadata }),
      redirectUri,
      postLogoutRedirectUri,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: "sessionStorage",
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback: () => {},
      },
    },
  });

  // MSAL.js v3+ requires an explicit async initialize() before any API call
  // (handleRedirectPromise, getAllAccounts, acquireTokenSilent, etc.).
  // Without this, components that fire MSAL APIs in mount effects throw
  // `uninitialized_public_client_application`, which surfaces in the login
  // page's alert as raw jargon. Awaiting here means callers of
  // getMsalInstance() always receive a fully-initialized instance.
  await instance.initialize();

  msalInstance = instance;
  return msalInstance;
}

/**
 * Return the cached MSAL instance, creating it if necessary.
 *
 * @returns {Promise<PublicClientApplication>} The MSAL instance
 */
export async function getMsalInstance() {
  if (msalInstance) return msalInstance;
  return initializeMsal();
}
